import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from '@/composables';

type TokenPredicateOption = {
  label: string;
  value: string;
};

export type FlowTokenModel = {
  predicateList: TokenPredicateOption[];
  predicate: string;
  parameter: string;
  selected: string[];
  values: Array<{ text: string; value: string }>;
  include_empty: boolean;
};

type FlowTokenModelsConditionProps = {
  conditions: Record<string, Array<{
    values: string[];
    parameter: string;
    predicate: string;
  }>>;
  conditionKey: 'queryHeader' | 'customToken';
};

export const useFlowTokenModelsCondition = (
  props: FlowTokenModelsConditionProps,
  emit: (event: 'close') => void,
) => {
  const { t } = useI18n();
  const tokenModels = ref<FlowTokenModel[]>([]);

  const emptyValue = computed(() => ({
    text: `[${t('Empty Value')}]`,
    value: '',
  }));

  const predicateList = computed<TokenPredicateOption[]>(() => [
    { label: t('Equals'), value: 'EQUALS' },
    { label: t('Contains'), value: 'CONTAINS' },
    { label: t('Not equal'), value: 'NOT_EQUALS' },
    { label: t('Not contain'), value: 'NOT_CONTAINS' },
  ]);

  const createTokenModel = (): FlowTokenModel => ({
    predicateList: predicateList.value,
    predicate: 'EQUALS',
    parameter: '',
    selected: [],
    values: [emptyValue.value],
    include_empty: false,
  });

  onMounted(() => {
    const existing = props.conditions[props.conditionKey];
    if (existing?.length) {
      tokenModels.value = existing.map((entry) => ({
        selected: entry.values,
        parameter: entry.parameter,
        predicate: entry.predicate,
        include_empty: false,
        predicateList: predicateList.value,
        values: [emptyValue.value],
      }));
    } else {
      tokenModels.value.push(createTokenModel());
    }
  });

  watch(
    tokenModels,
    () => {
      props.conditions[props.conditionKey] = tokenModels.value.map((entry) => ({
        values: entry.selected,
        parameter: entry.parameter,
        predicate: entry.predicate,
      }));
    },
    { deep: true },
  );

  const add = () => {
    tokenModels.value.push(createTokenModel());
  };

  const remove = (model: FlowTokenModel) => {
    if (tokenModels.value.length === 1) {
      emit('close');
      return;
    }

    tokenModels.value.splice(tokenModels.value.indexOf(model), 1);
  };

  return {
    t,
    tokenModels,
    emptyValue,
    add,
    remove,
  };
};
